import styled from '@emotion/styled';
import { EventTypes, VisibilityStates } from '@racemap/utilities/consts/events';
import { isDraftOrUnlistedOrArchivedOrNotActivated } from '@racemap/utilities/functions/event';
import {
  eventIsNeitherFreeNorPaid,
  isChildEvent,
  isGroupEvent,
} from '@racemap/utilities/functions/utils';
import type { RacemapEvent } from '@racemap/utilities/types/events';
import { Space, Tag } from 'antd';
import type { Immutable } from 'immer';
import { DateTime } from 'luxon';
import type { FC } from 'react';
import { useCurrentUser } from 'src/lib/customHooks/useCurrentUser';
import {
  IconContestGroup,
  IconFuture,
  IconInvisible,
  IconOk,
  IconPast,
  IconPlay,
  IconRegular,
  IconRepeat,
  IconSafemap,
  IconStageGroup,
  IconVisible,
} from '../Icon';

interface BadgesProps {
  event:
    | Pick<
        Immutable<RacemapEvent>,
        'startTime' | 'endTime' | 'authorization' | 'visibility' | 'type' | 'isRepeating'
      >
    | undefined;
  withEventTypeBadge?: boolean;
}

export const EventInfoBadges: FC<BadgesProps> = ({ event, withEventTypeBadge = false }) => {
  const user = useCurrentUser();
  if (user == null) return <></>;

  return (
    <Container>
      {withEventTypeBadge && <EventTypeBadge event={event} />}
      <RunningBadge event={event} />
      <PaymentStateBadge event={event} />
      <ArchivedBadge event={event} />
      <RepeatingBadge event={event} />
      {isChildEvent(event) && <GroupVisibilityBagde event={event} />}
    </Container>
  );
};

export const ArchivedBadge: FC<BadgesProps> = ({ event }) => {
  if (event == null) return null;
  if (event.visibility !== VisibilityStates.ARCHIVED) return null;

  return <Tag color="volcano">ARCHIVED</Tag>;
};

export const RepeatingBadge: FC<BadgesProps> = ({ event }) => {
  if (event == null) return null;
  if (!event.isRepeating) return null;

  return (
    <Tag color="cyan" icon={<IconRepeat />}>
      REPEATING
    </Tag>
  );
};

export const GroupVisibilityBagde: FC<BadgesProps> = ({ event }) => {
  if (event == null) return null;
  const isHidden = isDraftOrUnlistedOrArchivedOrNotActivated(event);

  return (
    <Tag icon={isHidden ? <IconInvisible /> : <IconVisible />} color="purple">
      {isHidden ? 'Hidden' : 'Visible'}
    </Tag>
  );
};

export const EventTypeBadge: FC<BadgesProps> = ({ event }: { event: BadgesProps['event'] }) => {
  if (event == null) return null;
  const eventType = event.type;
  const groupIndex = isChildEvent(event) ? event.index : undefined;

  switch (eventType) {
    case EventTypes.REGULAR:
      return (
        <Tag icon={<IconRegular />} color="default">
          SINGLE MAP
        </Tag>
      );
    case EventTypes.CONTEST_GROUP:
      return (
        <Tag icon={<IconContestGroup />} color="default">
          GROUP MAP
        </Tag>
      );
    case EventTypes.STAGE_GROUP:
      return (
        <Tag icon={<IconStageGroup />} color="default">
          STAGES
        </Tag>
      );
    case EventTypes.SAFEMAP:
      return (
        <Tag icon={<IconSafemap />} color="default">
          SAFEMAP
        </Tag>
      );
    case EventTypes.CONTEST:
      return (
        <Tag icon={<IconContestGroup />} color="default">
          CONTEST{groupIndex != null ? ` #${groupIndex + 1}` : ''}
        </Tag>
      );
    case EventTypes.STAGE:
      return (
        <Tag icon={<IconStageGroup />} color="default">
          STAGE{groupIndex != null ? ` #${groupIndex + 1}` : ''}
        </Tag>
      );
    default:
      return null;
  }
};

export const RunningBadge: FC<BadgesProps> = ({ event }) => {
  if (event == null) return null;
  if (event.startTime == null || event.endTime == null) return null;
  if (event.visibility === VisibilityStates.ARCHIVED) return null;

  const start = DateTime.fromISO(event.startTime);
  const end = DateTime.fromISO(event.endTime);

  if (DateTime.now() < start)
    return (
      <Tag icon={<IconFuture />} color="processing" bordered={false}>
        UPCOMING
      </Tag>
    );
  if (DateTime.now() > start && DateTime.now() < end)
    return (
      <Tag icon={<IconPlay />} color="success">
        LIVE
      </Tag>
    );
  if (DateTime.now() > end) return <Tag icon={<IconPast />}>FINISHED</Tag>;

  return null;
};

export const PaymentStateBadge: FC<BadgesProps> = ({ event }) => {
  if (event == null) return null;
  if (isGroupEvent(event)) return null;
  if (eventIsNeitherFreeNorPaid(event)) return <Tag color="warning">TRIAL</Tag>;
  if (event.visibility === VisibilityStates.ARCHIVED) return null;

  return (
    <Tag icon={<IconOk />} color="success">
      ACTIVE
    </Tag>
  );
};

const Container = styled(Space)`
  flex-wrap: nowrap;

  .ant-tag {
    margin-inline-end: unset;
  }

  svg {
    margin-right: 4px;
  }
`;
